import React, { useState } from 'react';

import api from '../../services/api';
import { subsectionNumber } from '../../utils/contentTree';
import GalleryImageProposalsEditor from './GalleryImageProposalsEditor';
import ContextualHierarchyCreatePanel from './ContextualHierarchyCreatePanel';
import HierarchyCreateActions from './HierarchyCreateActions';
import RichTextEditor from './RichTextEditor';
import VisualTableListEditor from './VisualTableListEditor';
import { getAnchoredTableIds, removeTableAnchor } from './bookTableAnchor';
import { normalizeImageSlots } from './contentLayout';

const cleanNumberedTitle = (title = '') => {
  const value = String(title).trim();
  const cleaned = value.replace(/^\d+(?:\.\d+)*\.?\s*/, '').trim();
  return cleaned || value;
};

const jsonText = (value, fallback) =>
  JSON.stringify(value ?? fallback, null, 2);

const draftFromNode = (node) => ({
  title: node.title || '',
  content: node.content || '',
  imagesText: jsonText(normalizeImageSlots(node.images), []),
  videosText: jsonText(node.videos, []),
  tablesText: jsonText(node.tables, []),
  imageConfigText: jsonText(node.image_display_config, {}),
  videoConfigText: jsonText(node.video_display_config, {}),
});

const parseJsonList = (value, label) => {
  let parsed;
  try {
    parsed = JSON.parse(value || '[]');
  } catch (error) {
    throw new Error(`${label} doit contenir un JSON valide.`);
  }
  if (!Array.isArray(parsed)) {
    throw new Error(`${label} doit contenir une liste JSON.`);
  }
  return parsed;
};

const parseJsonListOrEmpty = (value) => {
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseJsonObject = (value, label) => {
  let parsed;
  try {
    parsed = JSON.parse(value || '{}');
  } catch (error) {
    throw new Error(`${label} doit contenir un JSON valide.`);
  }
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error(`${label} doit contenir un objet JSON.`);
  }
  return parsed;
};

const mutationErrorMessage = (error, fallback) => {
  const imageError = error?.response?.data?.images;
  if (typeof imageError === 'string') return imageError;
  if (imageError) return JSON.stringify(imageError);
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

const RecursiveSubsectionChildren = ({
  parentNode,
  numberingPrefix,
  bookId,
  chapterId,
  sectionId,
  canEditStructure = false,
  selectedItem,
  selectedLang = 'source',
  sourceLanguage = 'fr',
  contentLanguage = 'fr',
  renderContent = () => null,
  renderImages = () => null,
  renderVideos = () => null,
  renderTables = () => null,
  onUploadImage,
  activeCreateTarget = null,
  creationLocked = false,
  onOpenCreateTarget = () => {},
  onCancelCreateTarget = () => {},
  onSubmitCreateTarget = async () => {},
  onChanged = async () => {},
}) => {
  const canManageImageStructure =
    canEditStructure &&
    (selectedLang === 'source' || selectedLang === sourceLanguage);
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [movingId, setMovingId] = useState(null);
  const [moveDrafts, setMoveDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [tableValidity, setTableValidity] = useState({});

  const hasInvalidTableLayout = (nodeId, tables) =>
    tables.some(
      (_, tableIndex) => tableValidity[`${nodeId}-${tableIndex}`] === false
    );

  const updateTableValidity = (nodeId, tableIndex, isValid) => {
    const key = `${nodeId}-${tableIndex}`;
    setTableValidity((current) =>
      current[key] === isValid ? current : { ...current, [key]: isValid }
    );
  };

  const detailUrl = (nodeId) =>
    `/books/${bookId}/chapters/${chapterId}/sections/${sectionId}/subsections/${nodeId}/`;

  const updateDraft = (nodeId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [nodeId]: {
        ...(current[nodeId] || {}),
        [field]: value,
      },
    }));
  };

  const beginEdit = (node) => {
    setDrafts((current) => ({
      ...current,
      [node.id]: current[node.id] || draftFromNode(node),
    }));
    setErrors((current) => ({ ...current, [node.id]: '' }));
    setEditingId(node.id);
  };

  const saveNode = async (node, parentId) => {
    const draft = drafts[node.id] || draftFromNode(node);
    try {
      const title = String(draft.title || '').trim();
      if (!title) {
        throw new Error('Le titre est requis.');
      }
      const tables = parseJsonList(draft.tablesText, 'Tableaux');
      if (hasInvalidTableLayout(node.id, tables)) {
        throw new Error(
          'Corrigez les largeurs de colonnes avant d’enregistrer.'
        );
      }
      const payload = {
        title,
        content: draft.content || '',
        images: parseJsonList(draft.imagesText, 'Images'),
        videos: parseJsonList(draft.videosText, 'Vidéos'),
        tables,
        image_display_config: parseJsonObject(
          draft.imageConfigText,
          'Configuration des images'
        ),
        video_display_config: parseJsonObject(
          draft.videoConfigText,
          'Configuration des vidéos'
        ),
      };
      const params =
        selectedLang && selectedLang !== 'source'
          ? { lang: selectedLang }
          : {};

      setSavingId(node.id);
      setErrors((current) => ({ ...current, [node.id]: '' }));
      await api.patch(
        detailUrl(node.id),
        payload,
        { params }
      );
      await onChanged({
        anchorIds: [
          `subsection-${node.id}`,
          parentId ? `subsection-${parentId}` : null,
          `section-${sectionId}`,
          `chapter-${chapterId}`,
        ].filter(Boolean),
      });
      setEditingId(null);
    } catch (error) {
      setErrors((current) => ({
        ...current,
        [node.id]: mutationErrorMessage(
          error,
          'La modification a échoué.'
        ),
      }));
    } finally {
      setSavingId(null);
    }
  };

  const deleteNode = async (node, number) => {
    if (!window.confirm(`Supprimer ${number} et tous ses sous-niveaux ?`)) {
      return;
    }
    try {
      setSavingId(`delete-${node.id}`);
      setErrors((current) => ({ ...current, [node.id]: '' }));
      await api.delete(detailUrl(node.id));
      await onChanged();
    } catch (error) {
      setErrors((current) => ({
        ...current,
        [node.id]:
          error?.response?.data?.error ||
          error?.message ||
          'La suppression a échoué.',
      }));
    } finally {
      setSavingId(null);
    }
  };

  const beginMove = (node, parentId) => {
    setMoveDrafts((current) => ({
      ...current,
      [node.id]: {
        targetSectionId: sectionId,
        targetParentId: parentId ?? '',
        order: node.order ?? 0,
      },
    }));
    setErrors((current) => ({ ...current, [node.id]: '' }));
    setMovingId(node.id);
  };

  const moveNode = async (
    node,
    {
      targetSectionId = sectionId,
      targetParentId = null,
      order = node.order ?? 0,
    }
  ) => {
    const parsedSectionId = Number(targetSectionId);
    const parsedOrder = Number(order);
    if (!Number.isInteger(parsedSectionId) || parsedSectionId <= 0) {
      setErrors((current) => ({
        ...current,
        [node.id]: 'La section cible est invalide.',
      }));
      return;
    }
    if (!Number.isInteger(parsedOrder) || parsedOrder < 0) {
      setErrors((current) => ({
        ...current,
        [node.id]: "L'ordre cible est invalide.",
      }));
      return;
    }

    const parsedParentId =
      targetParentId === '' || targetParentId === null
        ? null
        : Number(targetParentId);
    if (
      parsedParentId !== null &&
      (!Number.isInteger(parsedParentId) || parsedParentId <= 0)
    ) {
      setErrors((current) => ({
        ...current,
        [node.id]: 'Le parent cible est invalide.',
      }));
      return;
    }

    try {
      setSavingId(`move-${node.id}`);
      setErrors((current) => ({ ...current, [node.id]: '' }));
      await api.patch(`${detailUrl(node.id)}move/`, {
        target_section_id: parsedSectionId,
        target_parent_id: parsedParentId,
        order: parsedOrder,
      });
      await onChanged();
      setMovingId(null);
    } catch (error) {
      setErrors((current) => ({
        ...current,
        [node.id]:
          error?.response?.data?.error ||
          error?.response?.data?.parent_id ||
          error?.message ||
          'Le déplacement a échoué.',
      }));
    } finally {
      setSavingId(null);
    }
  };

  const renderNodes = (
    nodes = [],
    prefix,
    path = [],
    currentParentId = parentNode?.id,
    ancestors = parentNode ? [parentNode] : []
  ) =>
    nodes.map((node, index) => {
      const order = Number.isFinite(Number(node.order))
        ? Number(node.order)
        : index;
      const nodePath = [...path, order];
      const number = subsectionNumber(prefix, [order]);
      const title = cleanNumberedTitle(node.title);
      const depth = Math.max(node.depth || nodePath.length + 3, 3);
      const Heading = `h${Math.min(6, depth)}`;
      const isSelected =
        selectedItem?.type === 'subsection' &&
        selectedItem?.subsectionId === node.id;
      const isEditing = editingId === node.id;
      const draft = drafts[node.id] || draftFromNode(node);
      const draftTables = parseJsonListOrEmpty(draft.tablesText);
      const nodeTables = Array.isArray(node.tables) ? node.tables : [];
      const anchoredTableIds = getAnchoredTableIds(
        node.content,
        nodeTables
      );
      const hasInvalidTables = hasInvalidTableLayout(node.id, draftTables);
      const moveDraft = moveDrafts[node.id] || {
        targetSectionId: sectionId,
        targetParentId: currentParentId ?? '',
        order,
      };
      const hierarchyContext = {
        currentType: 'subsection',
        currentItem: node,
        chapter: { id: chapterId },
        section: { id: sectionId },
        subsectionAncestors: ancestors,
      };
      const sourceKey = `subsection:${node.id}`;

      return (
        <section
          id={`subsection-${node.id}`}
          key={node.id ?? nodePath.join('-')}
          data-hierarchy-item={sourceKey}
          className={`recursive-subsection-content min-w-0 max-w-full ${
            isSelected ? 'recursive-subsection-content--selected' : ''
          }`}
          style={{
            marginTop: '16px',
            paddingLeft: `${Math.min(Math.max(depth - 4, 0) * 12, 36)}px`,
            borderLeft: '2px solid #e5e7eb',
          }}
        >
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <Heading className="min-w-0 break-words font-semibold text-gray-700">
              {number} {title}
            </Heading>

            {canEditStructure && (
              <div
                role="toolbar"
                aria-label={`Actions pour ${number}`}
                className="flex w-full min-w-0 flex-wrap justify-start gap-2 sm:w-auto sm:justify-end [&>button]:shrink-0 [&>button]:whitespace-nowrap"
              >
                <button
                  type="button"
                  className="rounded bg-blue-600 px-2 py-1 text-xs text-white"
                  aria-label={`Modifier ${number}`}
                  onClick={() => beginEdit(node)}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="rounded bg-gray-600 px-2 py-1 text-xs text-white disabled:opacity-40"
                  aria-label={`Monter ${number}`}
                  disabled={index === 0 || savingId === `move-${node.id}`}
                  onClick={() =>
                    moveNode(node, {
                      targetSectionId: sectionId,
                      targetParentId: currentParentId,
                      order: index - 1,
                    })
                  }
                >
                  Monter
                </button>
                <button
                  type="button"
                  className="rounded bg-gray-600 px-2 py-1 text-xs text-white disabled:opacity-40"
                  aria-label={`Descendre ${number}`}
                  disabled={
                    index === nodes.length - 1 ||
                    savingId === `move-${node.id}`
                  }
                  onClick={() =>
                    moveNode(node, {
                      targetSectionId: sectionId,
                      targetParentId: currentParentId,
                      order: index + 1,
                    })
                  }
                >
                  Descendre
                </button>
                <button
                  type="button"
                  className="rounded bg-amber-600 px-2 py-1 text-xs text-white"
                  aria-label={`Déplacer ${number}`}
                  onClick={() => beginMove(node, currentParentId)}
                >
                  Déplacer
                </button>
                <button
                  type="button"
                  className="rounded bg-red-600 px-2 py-1 text-xs text-white"
                  aria-label={`Supprimer ${number}`}
                  disabled={savingId === `delete-${node.id}`}
                  onClick={() => deleteNode(node, number)}
                >
                  Supprimer
                </button>
              </div>
            )}
          </div>

          {canEditStructure && (
            <div className="mt-2 grid min-w-0 gap-2">
              <HierarchyCreateActions
                label={number}
                context={hierarchyContext}
                activeActionKey={activeCreateTarget?.actionKey || ''}
                creationLocked={creationLocked || Boolean(activeCreateTarget)}
                onOpen={onOpenCreateTarget}
              />
              {activeCreateTarget?.sourceKey === sourceKey && (
                <ContextualHierarchyCreatePanel
                  key={activeCreateTarget.actionKey}
                  target={activeCreateTarget}
                  onUploadImage={onUploadImage}
                  onSubmit={onSubmitCreateTarget}
                  onCancel={onCancelCreateTarget}
                />
              )}
            </div>
          )}

          {isEditing ? (
            <div className="mt-3 grid min-w-0 max-w-full gap-3 rounded border border-blue-200 bg-blue-50 p-3">
              <label className="grid gap-1 text-sm">
                <span>Titre</span>
                <input
                  aria-label={`Titre ${number}`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={draft.title}
                  onChange={(event) =>
                    updateDraft(node.id, 'title', event.target.value)
                  }
                />
              </label>
              <div className="grid gap-1 text-sm">
                <span>Contenu</span>
                <RichTextEditor
                  content={draft.content}
                  images={parseJsonListOrEmpty(draft.imagesText)}
                  tables={draftTables}
                  onChange={(content) =>
                    updateDraft(node.id, 'content', content)
                  }
                  onImagesChange={(images) =>
                    updateDraft(
                      node.id,
                      'imagesText',
                      JSON.stringify(images, null, 2)
                    )
                  }
                  onTablesChange={(tables) =>
                    updateDraft(
                      node.id,
                      'tablesText',
                      JSON.stringify(tables, null, 2)
                    )
                  }
                  onUploadImage={onUploadImage}
                  contentLanguage={contentLanguage}
                  canManageImages={canEditStructure}
                  canManageImageStructure={canManageImageStructure}
                  placeholder={`Écrivez le contenu de ${number} ici...`}
                  ariaLabel={`Contenu ${number}`}
                  editable
                />
                <div className="mt-3 space-y-3">
                  <h6 className="text-sm font-semibold text-gray-800">
                    Propositions des images de galerie
                  </h6>
                  <GalleryImageProposalsEditor
                    images={parseJsonListOrEmpty(draft.imagesText)}
                    onChange={(images) =>
                      updateDraft(
                        node.id,
                        'imagesText',
                        JSON.stringify(images, null, 2)
                      )
                    }
                    onUploadImage={onUploadImage}
                    canEdit={canEditStructure}
                    canManageStructure={canManageImageStructure}
                  />
                </div>
              </div>

              <VisualTableListEditor
                tables={draftTables}
                onChange={(tables) =>
                  updateDraft(
                    node.id,
                    'tablesText',
                    JSON.stringify(tables, null, 2)
                  )
                }
                onValidityChange={(tableIndex, isValid) =>
                  updateTableValidity(node.id, tableIndex, isValid)
                }
                onDeleteTable={(tableId) =>
                  setDrafts((current) => {
                    const currentDraft =
                      current[node.id] || draftFromNode(node);
                    return {
                      ...current,
                      [node.id]: {
                        ...currentDraft,
                        content: removeTableAnchor(
                          currentDraft.content,
                          tableId
                        ),
                      },
                    };
                  })
                }
              />

              <details>
                <summary className="cursor-pointer text-sm font-medium">
                  Vidéos et configurations avancées
                </summary>
                <div className="mt-2 grid gap-2">
                  {[
                    ['videosText', 'Vidéos'],
                  ].map(([field, label]) => (
                    <label key={field} className="grid gap-1 text-sm">
                      <span>{label}</span>
                      <textarea
                        aria-label={`${label} ${number}`}
                        className="min-h-24 rounded border border-gray-300 px-2 py-1 font-mono"
                        value={draft[field]}
                        readOnly={
                          field === 'imagesText' &&
                          !canManageImageStructure
                        }
                        onChange={(event) => {
                          if (
                            field === 'imagesText' &&
                            !canManageImageStructure
                          ) {
                            return;
                          }
                          updateDraft(node.id, field, event.target.value);
                        }}
                      />
                    </label>
                  ))}
                  {[
                    ['imageConfigText', 'Configuration des images'],
                    ['videoConfigText', 'Configuration des vidéos'],
                  ].map(([field, label]) => (
                    <label key={field} className="grid gap-1 text-sm">
                      <span>{label}</span>
                      <textarea
                        aria-label={`${label} ${number}`}
                        className="min-h-20 rounded border border-gray-300 px-2 py-1 font-mono"
                        value={draft[field]}
                        onChange={(event) =>
                          updateDraft(node.id, field, event.target.value)
                        }
                      />
                    </label>
                  ))}
                </div>
              </details>

              {errors[node.id] && (
                <p role="alert" className="text-sm text-red-700">
                  {errors[node.id]}
                </p>
              )}
              {hasInvalidTables && (
                <p role="alert" className="text-sm text-red-700">
                  Corrigez les largeurs de colonnes avant d’enregistrer.
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
                  aria-label={`Enregistrer les modifications ${number}`}
                  disabled={savingId === node.id || hasInvalidTables}
                  onClick={() => saveNode(node, currentParentId)}
                >
                  Enregistrer
                </button>
                <button
                  type="button"
                  className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700"
                  onClick={() => setEditingId(null)}
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <>
              {node.content && (
                <div className="mt-2">
                  {renderContent(node.content, node.images || [], nodeTables)}
                </div>
              )}
              {renderImages(node.images || [], node.image_display_config || {})}
              {renderVideos(node.videos || [], node.video_display_config || {})}
              {renderTables(
                nodeTables.filter((table) => !anchoredTableIds.has(table?.id))
              )}
            </>
          )}

          {movingId === node.id && (
            <div className="mt-3 grid min-w-0 max-w-full gap-2 rounded border border-amber-200 bg-amber-50 p-3">
              <label className="grid gap-1 text-sm">
                <span>ID de la section cible</span>
                <input
                  type="number"
                  min="1"
                  aria-label={`Section cible ${number}`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={moveDraft.targetSectionId}
                  onChange={(event) =>
                    setMoveDrafts((current) => ({
                      ...current,
                      [node.id]: {
                        ...moveDraft,
                        targetSectionId: event.target.value,
                      },
                    }))
                  }
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span>ID du parent cible (vide pour la racine)</span>
                <input
                  type="number"
                  min="1"
                  aria-label={`Parent cible ${number}`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={moveDraft.targetParentId}
                  onChange={(event) =>
                    setMoveDrafts((current) => ({
                      ...current,
                      [node.id]: {
                        ...moveDraft,
                        targetParentId: event.target.value,
                      },
                    }))
                  }
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span>Position cible</span>
                <input
                  type="number"
                  min="0"
                  aria-label={`Position cible ${number}`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={moveDraft.order}
                  onChange={(event) =>
                    setMoveDrafts((current) => ({
                      ...current,
                      [node.id]: {
                        ...moveDraft,
                        order: event.target.value,
                      },
                    }))
                  }
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded bg-amber-600 px-3 py-1 text-sm text-white"
                  aria-label={`Confirmer le déplacement ${number}`}
                  disabled={savingId === `move-${node.id}`}
                  onClick={() => moveNode(node, moveDraft)}
                >
                  Confirmer
                </button>
                <button
                  type="button"
                  className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700"
                  onClick={() => setMovingId(null)}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {errors[node.id] && !isEditing && (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {errors[node.id]}
            </p>
          )}

          {renderNodes(
            node.subsections || [],
            number,
            nodePath,
            node.id,
            [...ancestors, node]
          )}
        </section>
      );
    });

  return renderNodes(parentNode?.subsections || [], numberingPrefix);
};

export default RecursiveSubsectionChildren;
